import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Upload } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/sections/Footer';
import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ACCEPTED_CV_TYPES,
  CAREERS_DEPARTMENTS,
  CareersFormError,
  MAX_CV_SIZE_BYTES,
  submitCareersApplication,
  type CareersDepartment,
} from '@/lib/careersForm';

const careersSchema = z.object({
  firstName: z.string().trim().min(2).max(80),
  lastName: z.string().trim().min(2).max(80),
  phone: z
    .string()
    .trim()
    .min(8)
    .max(20)
    .regex(/^[+0-9\s()-]+$/, 'invalid_phone'),
  role: z.string().trim().min(2).max(120),
  department: z.enum(CAREERS_DEPARTMENTS),
});

type CareersFormValues = z.infer<typeof careersSchema>;

const LavoraConNoi = () => {
  const { t } = useTranslation(['lavoraConNoi', 'seo']);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [cvError, setCvError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<CareersFormValues>({
    resolver: zodResolver(careersSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '',
      role: '',
      department: undefined,
    },
  });

  const department = watch('department');

  const validateCv = (file: File | null): string | null => {
    if (!file) return t('lavoraConNoi:errors.cvRequired');
    if (!ACCEPTED_CV_TYPES.includes(file.type as (typeof ACCEPTED_CV_TYPES)[number])) {
      return t('lavoraConNoi:errors.cvType');
    }
    if (file.size > MAX_CV_SIZE_BYTES) {
      return t('lavoraConNoi:errors.cvSize');
    }
    return null;
  };

  const onCvChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setCvError(validateCv(file));
    setCvFile(file);
  };

  const onSubmit = async (values: CareersFormValues) => {
    const cvValidation = validateCv(cvFile);
    if (cvValidation) {
      setCvError(cvValidation);
      return;
    }
    if (!cvFile) return;

    setIsSubmitting(true);
    try {
      await submitCareersApplication({
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        role: values.role,
        department: values.department,
        cv: cvFile,
      });
      toast.success(t('lavoraConNoi:success'));
      reset();
      setCvFile(null);
      setCvError(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      if (error instanceof CareersFormError && error.code === 'not_configured') {
        toast.error(t('lavoraConNoi:errors.notConfigured'));
      } else {
        toast.error(t('lavoraConNoi:errors.submit'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main id="main-content" className="min-h-screen bg-background">
      <SEO
        title={t('seo:lavoraConNoi.title')}
        description={t('seo:lavoraConNoi.description')}
        keywords={t('seo:lavoraConNoi.keywords')}
        logicalPath="/lavora-con-noi"
      />
      <Header />
      <section className="py-32 px-6">
        <div className="mx-auto max-w-2xl">
          <div className="mb-10 text-center">
            <h1 className="mb-4 text-4xl font-semibold text-foreground md:text-5xl">
              {t('lavoraConNoi:title')}
            </h1>
            <p className="text-muted-foreground">{t('lavoraConNoi:subtitle')}</p>
          </div>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-6 rounded-lg border border-border bg-card p-6 md:p-8"
            noValidate
          >
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="firstName">{t('lavoraConNoi:fields.firstName')}</Label>
                <Input id="firstName" autoComplete="given-name" {...register('firstName')} />
                {errors.firstName && (
                  <p className="text-sm text-destructive">{t('lavoraConNoi:errors.required')}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">{t('lavoraConNoi:fields.lastName')}</Label>
                <Input id="lastName" autoComplete="family-name" {...register('lastName')} />
                {errors.lastName && (
                  <p className="text-sm text-destructive">{t('lavoraConNoi:errors.required')}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t('lavoraConNoi:fields.phone')}</Label>
              <Input id="phone" type="tel" autoComplete="tel" {...register('phone')} />
              {errors.phone && (
                <p className="text-sm text-destructive">
                  {errors.phone.message === 'invalid_phone'
                    ? t('lavoraConNoi:errors.phone')
                    : t('lavoraConNoi:errors.required')}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">{t('lavoraConNoi:fields.role')}</Label>
              <Input
                id="role"
                placeholder={t('lavoraConNoi:fields.rolePlaceholder')}
                {...register('role')}
              />
              {errors.role && (
                <p className="text-sm text-destructive">{t('lavoraConNoi:errors.required')}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="department">{t('lavoraConNoi:fields.department')}</Label>
              <Select
                value={department}
                onValueChange={(value) => setValue('department', value as CareersDepartment, { shouldValidate: true })}
              >
                <SelectTrigger id="department" aria-label={t('lavoraConNoi:fields.department')}>
                  <SelectValue placeholder={t('lavoraConNoi:fields.departmentPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {CAREERS_DEPARTMENTS.map((dept) => (
                    <SelectItem key={dept} value={dept}>
                      {t(`lavoraConNoi:departments.${dept}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.department && (
                <p className="text-sm text-destructive">{t('lavoraConNoi:errors.required')}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="cv">{t('lavoraConNoi:fields.cv')}</Label>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <input
                  ref={fileInputRef}
                  id="cv"
                  name="cv"
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="sr-only"
                  onChange={onCvChange}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full sm:w-auto"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
                  {t('lavoraConNoi:fields.cvButton')}
                </Button>
                <p className="text-sm text-muted-foreground">
                  {cvFile ? cvFile.name : t('lavoraConNoi:fields.cvHint')}
                </p>
              </div>
              {cvError && <p className="text-sm text-destructive">{cvError}</p>}
            </div>

            <p className="text-xs text-muted-foreground">{t('lavoraConNoi:privacyNote')}</p>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? t('lavoraConNoi:submitting') : t('lavoraConNoi:submit')}
            </Button>
          </form>
        </div>
      </section>
      <Footer />
    </main>
  );
};

export default LavoraConNoi;
